import { Component, computed, inject, input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ButtonDirective } from '@innersource/common-ui-components';
import { PaginationStore } from '../pagination-store';
import { getJobRunPresentation, isFinishedJobStatus } from '../job-run-presentation';
import { getDisplayName, Job, Tool, ToolJob } from '../models/tool.models';
import { ToolchainJob } from '../models/toolchain.models';
import { CapabilityStore } from '../../services/capability.store';
import { timelinePreviewEntries } from '../../capability/job-page/job-timeline/timeline-preview/timeline-preview.util';
import { formatDateTime } from '../helper';

const PAGE_SIZE = 8;
type JobHistoryStore =
  PaginationStore<Job> | PaginationStore<ToolJob> | PaginationStore<ToolchainJob>;

@Component({
  selector: 'app-job-run-history',
  templateUrl: './job-run-history.component.html',
  styleUrls: ['./job-run-history.component.scss'],
  imports: [ButtonDirective, RouterLink],
})
export class JobRunHistoryComponent {
  /** Inputs. */
  readonly paginationStore = input.required<JobHistoryStore>();
  readonly emptyMessage = input('No runs yet.');

  /** Services. */
  private readonly capabilityStore = inject(CapabilityStore);

  /** Template Forwards. */
  readonly formatDateTime = formatDateTime;

  /** State. */
  readonly skeletonItems = Array.from({ length: PAGE_SIZE }, (_, index) => index);

  /** Returns the display data for the loaded job runs. */
  readonly runs = computed(() =>
    this.paginationStore()
      .items()
      .map(job => {
        const isToolJob = 'toolJobId' in job;
        const id = isToolJob ? job.toolJobId : job.toolchainJobId;
        const referenceId = isToolJob ? job.tool.toolId : job.toolchain.toolchainId;
        const resource = isToolJob
          ? this.capabilityStore.tool(job.tool.toolId)
          : this.capabilityStore.toolchain(job.toolchain.toolchainId);
        const presentation = getJobRunPresentation(job, resource && getDisplayName(resource));

        return {
          id,
          ...presentation,
          status:
            !isToolJob && !isFinishedJobStatus(job.status)
              ? `${presentation.status} (${job.currentStep}/${job.totalSteps})`
              : presentation.status,
          modified: job.modified,
          routerLink: isToolJob
            ? `/tools/${referenceId}/results/${id}`
            : `/toolchains/${referenceId}/results/${id}`,
          parameterSummary: this.parameterSummary(job),
        };
      })
  );

  /** Returns a job's input parameter summary. */
  private parameterSummary(job: ToolJob | ToolchainJob): string {
    const toolJob = 'toolJobId' in job ? job : job.toolJobs[0];
    const entries = timelinePreviewEntries(toolJob?.funcInput, this.toolFor(job, toolJob)?.inputs);
    if (!entries.length) return 'Parameters: None';
    return entries.map(entry => `${entry.label}: ${entry.value}`).join('    ·    ');
  }

  /** Returns the tool associated with a job. */
  private toolFor(job: ToolJob | ToolchainJob, toolJob?: ToolJob | null): Tool | undefined {
    if (!toolJob) return undefined;
    if ('toolchainJobId' in job) {
      const toolchain = this.capabilityStore.toolchain(job.toolchain.toolchainId);
      return toolchain?.tools[toolJob.tool.toolId];
    }
    return this.capabilityStore.tool(toolJob.tool.toolId);
  }
}
